import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, MapPin, CheckCircle, AlertCircle, Calendar, User, Clock, Phone, FileText } from 'lucide-react';
import { DriverProvider, useDriver } from '../../contexts/DriverContext';
import driverPickupService from '../../services/driverPickupService';
import { generateDriverRouteSheetPDF } from '../../components/ui/DriverRouteSheetPDF';
import { supabase } from '../../lib/supabase';
import { realtimeService } from '../../services/realtimeService';

function DriverPersonalDashboardContent() {
  const navigate = useNavigate();
  const { employee, signOut, loading: driverLoading } = useDriver();
  const [pickupTasks, setPickupTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    pending: 0,
    completed: 0,
    today: 0,
    total: 0,
    inProgress: 0
  });
  const [viewType, setViewType] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [driverUserId, setDriverUserId] = useState(null);

  useEffect(() => {
    if (!driverLoading && !employee) {
      navigate('/driver-login');
      return;
    }

    if (employee) {
      loadPickupTasks();
    }
  }, [employee, driverLoading, navigate]);

  // Get driver user ID on mount
  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        setDriverUserId(user?.id);
      }
    };
    getUserId();
  }, []);

  // Subscribe to real-time pickup tasks updates (filtered by driver_user_id)
  useEffect(() => {
    if (!employee || !driverUserId) return;

    const subscription = realtimeService?.subscribeToPickupTasks((payload) => {
      if (payload?.eventType === 'INSERT') {
        setPickupTasks(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setPickupTasks(prev => prev?.map(task =>
          task?.id === payload?.new?.id ? payload?.new : task
        ));
      } else if (payload?.eventType === 'DELETE') {
        setPickupTasks(prev => prev?.filter(task => task?.id !== payload?.old?.id));
      }
    }, driverUserId);

    return () => {
      realtimeService?.unsubscribe('pickup-tasks-realtime');
    };
  }, [employee, driverUserId]);

  const loadPickupTasks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const tasksData = await driverPickupService?.getPickupTasks(employee?.id);
      setPickupTasks(tasksData || []);
      
      // Calculate stats
      const newStats = {
        total: tasksData?.length || 0,
        completed: tasksData?.filter(t => t?.status === 'termine')?.length || 0,
        pending: tasksData?.filter(t => t?.status === 'planifie')?.length || 0,
        inProgress: tasksData?.filter(t => t?.status === 'en_cours')?.length || 0,
        today: tasksData?.filter(t => t?.date === getTodayString())?.length || 0
      };
      setStats(newStats);
    } catch (err) {
      console.error('Load tasks error:', err);
      setError('Erreur de chargement des collectes');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/driver-login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleTaskClick = (taskId) => {
    navigate(`/driver-pickup-detail-with-integrated-signature/${taskId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'termine': return 'bg-green-100 text-green-800 border-green-200';
      case 'en_cours': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'annule': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'termine': return <CheckCircle className="h-4 w-4" />;
      case 'en_cours': return <Calendar className="h-4 w-4" />;
      case 'annule': return <AlertCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'termine': return 'Terminé';
      case 'en_cours': return 'En cours';
      case 'annule': return 'Annulé';
      default: return 'Planifié';
    }
  };

  const handleGeneratePDF = () => {
    if (!employee) {
      alert('Impossible de générer la feuille de route sans informations chauffeur');
      return;
    }

    const filteredTasks = getFilteredTasks();
    if (filteredTasks?.length === 0) {
      alert('Aucune collecte à afficher pour la période sélectionnée');
      return;
    }

    generateDriverRouteSheetPDF(employee, filteredTasks, selectedDate, viewType);
  };

  const getFilteredTasks = () => {
    const date = new Date(selectedDate);
    
    if (viewType === 'daily') {
      return pickupTasks?.filter(p => p?.date === selectedDate);
    } else if (viewType === 'weekly') {
      const startOfWeek = new Date(date);
      startOfWeek?.setDate(date?.getDate() - date?.getDay() + 1);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek?.setDate(startOfWeek?.getDate() + 6);
      
      return pickupTasks?.filter(p => {
        const pickupDate = new Date(p?.date);
        return pickupDate >= startOfWeek && pickupDate <= endOfWeek;
      });
    } else if (viewType === 'monthly') {
      return pickupTasks?.filter(p => {
        const pickupDate = new Date(p?.date);
        return pickupDate?.getMonth() === date?.getMonth() && 
               pickupDate?.getFullYear() === date?.getFullYear();
      });
    }
    return pickupTasks;
  };

  const formatDateDisplay = () => {
    const date = new Date(selectedDate);
    if (viewType === 'daily') {
      return date?.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    } else if (viewType === 'weekly') {
      const startOfWeek = new Date(date);
      startOfWeek?.setDate(date?.getDate() - date?.getDay() + 1);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek?.setDate(startOfWeek?.getDate() + 6);
      return `Semaine du ${startOfWeek?.toLocaleDateString('fr-FR')} au ${endOfWeek?.toLocaleDateString('fr-FR')}`;
    } else if (viewType === 'monthly') {
      return date?.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }
  };

  function getTodayString() {
    return new Date()?.toISOString()?.split('T')?.[0];
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-green-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <User className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Bonjour, {employee?.name || 'Chauffeur'}</h1>
                <p className="text-sm text-green-100">
                  {employee?.zone ? `Zone: ${employee?.zone}` : 'ADT Express'}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <Calendar className="h-5 w-5" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Planning View Controls */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vue</label>
              <select
                value={viewType}
                onChange={(e) => setViewType(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="daily">Journalier</option>
                <option value="weekly">Hebdomadaire</option>
                <option value="monthly">Mensuel</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={handleGeneratePDF}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                📄 Générer PDF
              </button>
            </div>

            <div className="flex items-end">
              <div className="w-full text-center">
                <p className="text-sm text-gray-600">{formatDateDisplay()}</p>
                <p className="font-semibold text-gray-900">{getFilteredTasks()?.length} collecte(s)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total</div>
            <div className="text-2xl font-bold text-gray-900">{stats?.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Planifié</div>
            <div className="text-2xl font-bold text-gray-600">{stats?.pending}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-blue-600 mb-1">En cours</div>
            <div className="text-2xl font-bold text-blue-600">{stats?.inProgress}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-green-600 mb-1">Terminé</div>
            <div className="text-2xl font-bold text-green-600">{stats?.completed}</div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Collectes du {getTodayString()}
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-green-500 border-t-transparent"></div>
              <p className="mt-2 text-gray-600">Chargement...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-2" />
              <p className="text-red-600">{error}</p>
            </div>
          ) : pickupTasks?.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">Aucune collecte pour cette date</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {pickupTasks?.map((task) => (
                <li 
                  key={task?.id}
                  onClick={() => handleTaskClick(task?.id)}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Time Window */}
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-1">
                        <Clock className="h-4 w-4 text-green-600" />
                        {task?.timeWindow || 'Non défini'}
                      </div>

                      {/* Client Info */}
                      <div className="text-base font-semibold text-gray-900 mb-2">
                        {task?.clientName}
                      </div>

                      {/* Address */}
                      <div className="flex items-start gap-2 text-sm text-gray-600 mb-1">
                        <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <div>
                          <div>{task?.clientAddress}</div>
                          <div>{task?.clientCity} {task?.clientZone && `- ${task?.clientZone}`}</div>
                        </div>
                      </div>

                      {/* Phone */}
                      {task?.clientPhone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="h-4 w-4" />
                          <a 
                            href={`tel:${task?.clientPhone}`}
                            className="text-green-600 hover:text-green-700"
                            onClick={(e) => e?.stopPropagation()}
                          >
                            {task?.clientPhone}
                          </a>
                        </div>
                      )}

                      {/* Shipment Reference */}
                      {task?.shipmentReference && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                          <FileText className="h-4 w-4" />
                          Envoi: {task?.shipmentReference}
                        </div>
                      )}
                    </div>

                    {/* Status Badge */}
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(task?.status)}`}>
                      {getStatusIcon(task?.status)}
                      {getStatusLabel(task?.status)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// Wrap the component with DriverProvider
export default function DriverPersonalDashboard() {
  return (
    <DriverProvider>
      <DriverPersonalDashboardContent />
    </DriverProvider>
  );
}